import { useEffect, useState, type FormEvent } from "react";
import { Api, ApiError } from "../sync/api";
import { Field, Form } from "./ui";
import { t } from "../i18n";
import { logo, useBranding } from "../brand";
import { LanguageSwitch } from "./LanguageSwitch";

type Info = { workspace: string; role: string; invited_by: string; email?: string };

// AcceptInvite is where an invitation's link leads: who invited one to
// which workspace, and making one's account there.
export function AcceptInvite({ token, onSignedIn }: { token: string; onSignedIn: (token: string) => void }) {
  const brand = useBranding();
  const [info, setInfo] = useState<Info | null>(null);
  const [bad, setBad] = useState(false);
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const api = new Api("/api/v1", null);

  useEffect(() => {
    api.request<{ invite: Info }>("GET", `/invites/${encodeURIComponent(token)}`).then(
      ({ invite }) => setInfo(invite),
      () => setBad(true),
    );
  }, [token]);

  async function join(e: FormEvent) {
    e.preventDefault();
    if (password !== again) return setError(t("The passwords are not the same."));
    setBusy(true);
    setError("");
    try {
      const r = await api.request<{ token: string }>("POST", `/invites/${encodeURIComponent(token)}/accept`, {
        handle: handle.trim().toLowerCase(),
        display_name: name.trim(),
        password,
      });
      onSignedIn(r.token);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("Could not reach the server."));
      setBusy(false);
    }
  }

  return (
    <main className="page login">
      <div className="card login-card">
        <img className="login-logo" src={logo(192, brand)} alt="" width={44} height={44} />
        {bad ? (
          <>
            <h1>{t("This invitation does not work")}</h1>
            <p className="login-lede">{t("It may have expired, been used up or been taken back. Ask whoever sent it for a new one.")}</p>
            <a className="btn" href="/">
              {t("Go to sign in")}
            </a>
          </>
        ) : !info ? (
          <p className="login-lede">{t("Loading…")}</p>
        ) : (
          <>
            <h1>{t("Join {workspace}", { workspace: info.workspace })}</h1>
            <p className="login-lede">
              {info.role === "guest"
                ? t("{name} invited you as a guest, to the channels they chose. Make your account to start.", { name: info.invited_by })
                : t("{name} invited you. Make your account to start.", { name: info.invited_by })}
            </p>
            <Form onSubmit={join}>
              <Field label={t("Your name")}>
                <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoComplete="name" autoFocus required />
              </Field>
              <Field label={t("Handle")} hint={t("How people @mention you: lowercase letters, digits, '.', '_' or '-'.")}>
                <input
                  value={handle}
                  onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/\s+/g, "."))}
                  maxLength={64}
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                />
              </Field>
              <Field label={t("Password")} hint={t("At least 12 characters.")}>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={12} autoComplete="new-password" required />
              </Field>
              <Field label={t("Password again")}>
                <input type="password" value={again} onChange={(e) => setAgain(e.target.value)} minLength={12} autoComplete="new-password" required />
              </Field>
              {error && (
                <p className="error" role="alert">
                  {error}
                </p>
              )}
              <button type="submit" disabled={busy}>
                {busy ? t("Joining…") : t("Join {workspace}", { workspace: info.workspace })}
              </button>
            </Form>
          </>
        )}
      </div>
      <LanguageSwitch />
    </main>
  );
}
